import dayjs, { Dayjs } from "dayjs";
import { ChipProps } from "@mui/material/Chip";
import {
  PricebookEffectiveMode,
  PricebookValidity,
} from "../../../types/price-book.type";
import { formatPeriodDate, formatShortDate } from "./Periods/periodModel";

/**
 * Where a book's validity stands on a Bangkok day: `always` has no bounds, `upcoming` has not
 * started, `active` prices today, `ended` is past its last day.
 */
export type ValidityState = "always" | "upcoming" | "active" | "ended";

export const VALIDITY_STATE_COLOR: Record<ValidityState, ChipProps["color"]> = {
  always: "default",
  upcoming: "info",
  active: "success",
  ended: "default",
};

/** The mode a stored validity was set with: no first day = ทันที, no last day = ล่วงหน้า. */
export const toEffectiveMode = (
  validity: PricebookValidity,
): PricebookEffectiveMode => {
  if (validity.effective_from === null) return "now";
  return validity.effective_to === null ? "date" : "range";
};

export const resolveValidityState = (
  validity: PricebookValidity,
  today: Dayjs,
): ValidityState => {
  if (validity.effective_from === null) return "always";
  if (dayjs(validity.effective_from).isAfter(today, "day")) return "upcoming";
  if (validity.effective_to && dayjs(validity.effective_to).isBefore(today, "day")) {
    return "ended";
  }
  return "active";
};

/** "ใช้ได้ 01/11/2026 – 30/11/2026" or "เริ่มใช้ 01/11/2026"; empty for a book valid every day. */
export const formatValidityText = (validity: PricebookValidity): string => {
  if (validity.effective_from === null) return "";
  const from = formatPeriodDate(validity.effective_from);
  if (validity.effective_to === null) return `เริ่มใช้ ${from}`;
  return `ใช้ได้ ${from} – ${formatPeriodDate(validity.effective_to)}`;
};

/** The list chip of a book that does not price today: "เริ่มใช้ 01/11" or "หมดช่วงใช้ 30/11". */
export const formatValidityChipLabel = (
  validity: PricebookValidity,
  today: Dayjs,
): string => {
  const state = resolveValidityState(validity, today);
  if (state === "upcoming" && validity.effective_from) {
    return `เริ่มใช้ ${formatShortDate(validity.effective_from)}`;
  }
  if (state === "ended" && validity.effective_to) {
    return `หมดช่วงใช้ ${formatShortDate(validity.effective_to)}`;
  }
  return "";
};

/**
 * What a shop on the book bills at outside its days: the API prices it at the face-price book.
 * `facePriceCode` names that book once the system settings are read.
 */
export const buildFacePriceFallbackNote = (
  validity: PricebookValidity,
  facePriceCode: string | undefined,
): string => {
  const facePrice = facePriceCode ? `ราคาหน้าซอง (${facePriceCode})` : "ราคาหน้าซอง";
  const outside =
    toEffectiveMode(validity) === "range"
      ? "ก่อนวันเริ่มและหลังวันสิ้นสุด"
      : "ก่อนวันเริ่มใช้";
  return `${outside} ร้านที่ผูกสมุดนี้ออกบิลที่${facePrice}`;
};
